import { MASTODON_SOCIAL_DOMAIN } from '@/constants/community'
import { getCommunityServiceBaseURL } from '@/lib/configs'
import { guidSchema } from '@/schemas/room'
import { API_CHECK_USER_COMMUNITY_PERMISSION } from '@/services/api'
import { getCurrentAvatarList } from '@/services/avatar'
import { getPublicRooms, Room } from '@/services/cms'
import { redirect } from 'next/navigation'

export const fetchCommunityAPI = <Response = Record<string, unknown>>(
  url: string,
  method: RequestInit['method'] = 'GET',
  payload: RequestInit['body'],
  params: Omit<RequestInit, 'method' | 'body'> = {},
  htcToken = '',
): Promise<{ status: number; results: Response; headers: Headers } | void> => {
  const init: RequestInit = {
    headers: { 'content-type': 'application/json' },
    method,
    ...params,
  }
  if (htcToken) {
    init.headers = {
      ...init.headers,
      ...{ authkey: htcToken },
    }
  }
  if (payload) {
    init.body = JSON.stringify(payload)
  }
  const communityServiceBaseURL = getCommunityServiceBaseURL()
  return fetch(`${communityServiceBaseURL}${url}`, init)
    .then(async (response) => {
      const result = await response.text()
      try {
        return { status: response.status, results: JSON.parse(result), headers: response.headers }
      } catch (e) {
        // Don't return json.
        return { status: response.status, results: result, headers: response.headers }
      }
    })
    .catch((error) => {
      console.error('fetchCMSApiWithStatusCode error:', error)
    })
}

export const fetchCheckUserCommunityPermission = async (htcToken: string) => {
  const url = API_CHECK_USER_COMMUNITY_PERMISSION
  const res = await fetchCommunityAPI(url, 'Get', null, {}, htcToken)
  return res
}

export type Following = {
  domainName: string
  name: string
  nickName: string
  preferredUsername: string
  userIri: string
}

export type PaginationData = {
  from: number
  size: number
  total: number
}

export type FollowingsData = Following | FollowingWithIcon | FollowingWithRooms

export type FollowingsResults = Partial<PaginationData> & {
  data: FollowingsData[]
}

export type FollowingWithAccountId = Following & {
  htcAccountId: string
}

export type FollowingWithIcon = FollowingWithAccountId & {
  headIconUrl: string
}

export type FollowingWithRooms = FollowingWithAccountId & {
  publicRooms: Room[]
}

export async function getFollowings(htcToken: string, from: number = 0, size: number = 0) {
  return fetchCommunityAPI<FollowingsResults>(
    `/api/community-service/v1/followings?from=${from}&size=${size}`,
    'GET',
    null,
    {},
    htcToken,
  )
}

export type FollowingsDataWithAccountId = FollowingsData & { htcAccountId: string }

const mapFollowingsToAccountIds = (data: FollowingsData[]): FollowingsDataWithAccountId[] =>
  data?.map((item) => ({
    ...item,
    htcAccountId: item.userIri?.split('/').pop() || '',
  })) || []

export const getFollowingsWithAccountId = async (htcToken: string) => {
  const allFollowings: FollowingsData[] = []
  const size = 50

  let from = 0
  let hasMore = true

  while (hasMore) {
    try {
      const response = await getFollowings(htcToken, from, size)
      const currentPageData = response?.results?.data
      const total = response?.results?.total || 0

      if (!currentPageData || !Array.isArray(currentPageData)) {
        break
      }

      allFollowings.push(...currentPageData)

      if (total > 0) {
        hasMore = allFollowings.length < total
      } else {
        // Fallback to the original logic if total is not available
        hasMore = currentPageData.length === size
      }

      if (hasMore) {
        from += size
      }
    } catch (error) {
      console.error('Error fetching followings:', error)
      break
    }
  }

  return mapFollowingsToAccountIds(allFollowings)
}

function getHtcAccountIdsFromFollowings(followings: Following[]) {
  const communityServiceBaseURL = getCommunityServiceBaseURL()

  return followings.map((following) => {
    const htcAccountId = following.userIri.replace(`${communityServiceBaseURL}/users/`, '')
    return htcAccountId
  })
}

export async function getFollowingsWithIcon(htcToken: string, from: number = 0, size: number = 0) {
  const data = await getFollowings(htcToken, from, size)
  const allowedData = data?.results?.data?.filter(
    ({ domainName }) => !domainName.includes(MASTODON_SOCIAL_DOMAIN),
  )

  if (allowedData && allowedData.length < 1) {
    return { data: [] }
  }

  const htcAccountIds = getHtcAccountIdsFromFollowings(allowedData || [])
  const avatarResponse = await getCurrentAvatarList(htcToken, htcAccountIds)
  const finalData: FollowingWithIcon[] = []

  if (avatarResponse?.status === 200 && avatarResponse.results.data) {
    const avatarsData = avatarResponse.results.data.Avatars

    allowedData?.forEach((follower, i) => {
      const avatar = avatarsData.find(
        ({ ViveportId }: { ViveportId: string }) => ViveportId === htcAccountIds[i],
      )
      if (avatar) {
        finalData.push({
          ...follower,
          htcAccountId: htcAccountIds[i],
          headIconUrl: avatar.HeadIconDataUrl,
        })
      }
    })
  }

  return { ...data?.results, data: finalData }
}

export async function getFollowingsWithRooms(
  htcToken: string,
  totalVramKb: number,
  from: number = 0,
  size: number = 0,
) {
  const followingsResponse = await getFollowings(htcToken, from, size)
  const communityServiceBaseURL = getCommunityServiceBaseURL()

  if (followingsResponse?.results && Array.isArray(followingsResponse.results.data)) {
    const followings = followingsResponse.results.data
    const visibleFollowings = followings.filter(
      ({ domainName }) => !domainName.includes(MASTODON_SOCIAL_DOMAIN),
    )

    if (visibleFollowings.length > 0) {
      const followingsWithDetails = await Promise.all(
        visibleFollowings.map(async (following) => {
          const htcAccountId = following.userIri.replace(`${communityServiceBaseURL}/users/`, '')
          const response = await getPublicRooms(
            { htc_account_id: htcAccountId, total_vram_kb: totalVramKb },
            htcToken,
          )
          if (response?.status !== 200) return following

          return {
            ...following,
            publicRooms: response.results.results,
            htcAccountId,
          }
        }),
      )
      followingsResponse.results.data = followingsWithDetails
    } else {
      followingsResponse.results.data = visibleFollowings
    }
  }

  return followingsResponse
}

export async function getFollowers(htcToken: string, from: number = 0, size: number = 0) {
  return fetchCommunityAPI<FollowingsResults>(
    `/api/community-service/v1/followers?from=${from}&size=${size}`,
    'GET',
    null,
    {},
    htcToken,
  )
}
export async function getFollowersWithIcon(htcToken: string, from: number = 0, size: number = 0) {
  const response = await getFollowers(htcToken, from, size)

  if (!response?.results || !Array.isArray(response.results.data)) {
    return { data: [] }
  }

  const data = response.results.data
  const allowedData = data.filter(({ domainName }) => !domainName.includes(MASTODON_SOCIAL_DOMAIN))

  if (allowedData.length < 1) {
    return { data: [] }
  }

  const htcAccountIds = getHtcAccountIdsFromFollowings(allowedData)
  const avatarResponse = await getCurrentAvatarList(htcToken, htcAccountIds)
  const finalData: FollowingWithIcon[] = []

  if (avatarResponse?.status === 200 && avatarResponse.results.data) {
    const avatarsData = avatarResponse.results.data.Avatars

    allowedData.forEach((follower, i) => {
      const avatar = avatarsData.find(
        ({ ViveportId }: { ViveportId: string }) => ViveportId === htcAccountIds[i],
      )
      if (avatar) {
        finalData.push({
          ...follower,
          htcAccountId: htcAccountIds[i],
          headIconUrl: avatar.HeadIconDataUrl,
        })
      }
    })
  }

  return { ...response?.results, data: finalData }
}

export async function postUnFollow(
  htcToken: string,
  targetUserIri?: string,
  htcAccountId?: string,
) {
  const communityServiceBaseURL = getCommunityServiceBaseURL()
  if (!targetUserIri && htcAccountId) {
    targetUserIri = `${communityServiceBaseURL}/users/${htcAccountId}`
  }
  return fetchCommunityAPI(
    '/api/community-service/v1/unfollow',
    'POST',
    {
      // @ts-ignore
      targetUserIri,
    },
    {},
    htcToken,
  )
}

export async function postFollow(htcToken: string, targetUserIri?: string, htcAccountId?: string) {
  const communityServiceBaseURL = getCommunityServiceBaseURL()

  if (!targetUserIri && htcAccountId) {
    targetUserIri = `${communityServiceBaseURL}/users/${htcAccountId}`
  }

  return fetchCommunityAPI(
    '/api/community-service/v1/follow',
    'POST',
    {
      // @ts-ignore
      targetUserIri,
    },
    {},
    htcToken,
  )
}

export type UserFollowResult = {
  '@context': string
  first: string
  id: string
  items: string[] | string
  last: string
  totalItems: number
  type: string
}

export async function getPublicFollowers(htcAccountId: string) {
  return fetchCommunityAPI<UserFollowResult>(`/users/${htcAccountId}/followers`, 'GET', null, {})
}

export async function getPublicFollowings(htcAccountId: string) {
  return fetchCommunityAPI<UserFollowResult>(`/users/${htcAccountId}/following`, 'GET', null, {})
}

type GetPointBalanceResult = {
  data: {
    pointBalance: number
    userId: string
  }
}

/**
 * Get user points balance
 * @see {@link https://htcsense.jira.com/wiki/spaces/NEOSTORE/pages/3479175176/Point+Service+API+Doc#%5BGET%5D-%2Fpriv%2Fpoint-service%2Fv1%2Fpoints%2Fhistory}
 */
export async function getPointBalance(htcToken: string) {
  return fetchCommunityAPI<GetPointBalanceResult>(
    '/api/point-service/v1/points',
    'GET',
    null,
    {},
    htcToken,
  )
}

function getCountFromResponse(
  response: void | {
    status: number
    results: UserFollowResult
    headers: Headers
  },
): number {
  // 🥷🏼 need to exclude /groups/ ourselves, because BE don't wanna handle this
  return response?.status === 200 && response?.results && response.results?.items
    ? Array.isArray(response.results.items)
      ? response.results.items?.filter((item) => !item.includes('/groups/')).length
      : response.results.items.includes('/groups/')
        ? 0
        : 1
    : 0
}

export async function getFollowCounts(htcAccountId: string) {
  const [followingsRes, followersRes] = await Promise.all([
    getPublicFollowings(htcAccountId),
    getPublicFollowers(htcAccountId),
  ])

  const followingsCount = getCountFromResponse(followingsRes)
  const followersCount = getCountFromResponse(followersRes)

  return {
    followingsCount,
    followersCount,
  }
}

export const getFollowersByRoomOwnerAccountId = async (roomData: Room | undefined) => {
  const res = await getPublicFollowers(roomData!.owner.htc_account_id)
  return res?.results
}

type WebfingerResult = {
  subject: string
  aliases: string[]
  links: {
    rel: string
    type: string
    href: string
  }[]
}

const getWebfinger = async (username: string) => {
  return fetchCommunityAPI<WebfingerResult>(
    `/.well-known/webfinger?resource=acct:${username}@${getCommunityServiceBaseURL()}`,
    'GET',
    null,
    {},
  )
}

export const getHtcAccountIdFromIdentifier = async (identifier: string) => {
  if (guidSchema.safeParse(identifier).success) {
    return identifier
  }

  const response = await getWebfinger(identifier)

  if (response?.status === 200) {
    const webfingerData = response.results
    let htcAccountId = ''

    if (webfingerData?.aliases[0]) {
      htcAccountId = webfingerData?.aliases[0]?.split('/')?.pop() || ''
    } else if (webfingerData?.links[0]) {
      htcAccountId = webfingerData?.links[0]?.href?.split('/')?.pop() || ''
    }

    if (htcAccountId) {
      return htcAccountId
    }
  }

  redirect('/')
}
